import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  AccordionSummary,
  Box,
  Card,
  Container,
  Grid,
  Icon,
  Typography,
} from '@material-ui/core';
import { Row } from 'components/Row';
import { CustomButton } from 'components/CustomButton';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  CONTRATO_ETAPAS_PARAMS_URL,
  CONTRATO_FLUXO_DESCENTRALIZADO,
  CONTRATO_PASSOS,
  TipoContrato,
  TipoFluxo,
  URL_ADITIVO_PARAM,
  URL_DISTRATO_PARAM,
  URL_ENTE_DESCENTRALIZADO,
  VersaoContratoEnum,
} from 'models/contratos';
import ControlledAccordions from 'pages/Contratos/components/Accordion';
import { CreateRouteParams, EditRouteParams } from '../../__types';
import { loadCidadesByEstadoId, loadEstados } from 'store/modules/LogradouroActions';
import SnackbarUtils from 'utils/SnackbarUtils';
import { loadContratoAdendos, resetFiltersDocumento } from 'store/modules/AllContratos/AllContratosActions';
import { loadContratoEntePrivado, openModalEntePrivadoAditivo, openModalEntePrivadoDistrato } from 'store/modules/ContratosFluxoDescentralizado/EntePrivado/EntePrivadoActions';
import { createAditivoDescentralizadoRoute, createDistratoDescentralizadoRoute, modelosDocumentosContratoRoute } from 'library/Routes';
import '../../index.scss'
interface Cidade {
  nome: string;
}

export const DocumentoEntePrivadoDescentralizado = () => {
  const [tipoFluxoFilter, setTipoFluxoFilter] = useState<number | null>(null);
  const [tipoEnteFilter, setTipoEnteFilter] = useState<number | null>(null);
  const params = useParams<CreateRouteParams & EditRouteParams>();
  const id = params.id || null;
  const languageValues = useSelector(
    (states: RootState) => states.languages.languageValues
  );
  const contratoEntePrivadoReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosEntePrivado.contratoEditing
  );
  const adendoAll = useSelector(
    (state: RootState) => state.allContratos.adendosAll
  );
  const adendoObject = useSelector(
    (state: RootState) => state.allContratos.adendoObject
  );
  const logradouro = useSelector((state: RootState) => state.logradouro);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const fluxoEntePrivado = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO);
  const programasFilter = contratoEntePrivadoReducer.programasEducacionais;

  const getUFByIdEstado = useCallback((idEstadoParam: number | null): string => {
    const estadoV = logradouro.estados.find((estado) => estado.id === idEstadoParam);
    return estadoV ? estadoV.sigla : '';
  }, [logradouro.estados]);


  const loadEstadosAll = useCallback(() => {
    dispatch(loadEstados(
      () => {
        const idEstado = contratoEntePrivadoReducer.idEstado;
        if (idEstado) {
          loadCidades(idEstado);
        }
      },
      (error: any) => {
        SnackbarUtils.enqueueSnackbar({
          message: error.response.statusText || languageValues.errorLoadEstados,
          options: {
            variant: 'error',
          },
        });
      }
    ));
    // eslint-disable-next-line
  }, [dispatch, contratoEntePrivadoReducer.idEstado, languageValues.errorLoadEstados]);

  const loadCidades = (idEstado: number) => {
    const uf = getUFByIdEstado(idEstado);
    if (uf && getCidades(uf).length === 0) {
      dispatch(loadCidadesByEstadoId(idEstado, uf));
    }
  };

  const getCidades = (uf: string): Cidade[] => {
    if (!uf) {
      return [];
    }
    return logradouro.cidades[uf.toLowerCase()] || [];
  };

  useEffect(() => {
    loadEstadosAll()
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id && fluxoEntePrivado) {
      dispatch(loadContratoAdendos(Number(id)));
    }
  }, [dispatch, fluxoEntePrivado, id]);

  useEffect(() => {
    dispatch(resetFiltersDocumento());
    const urlAtual = window.location.href;

    const isEntePrivado = () => {
      return urlAtual.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO) &&
        !urlAtual.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO) &&
        !urlAtual.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO) &&
        !urlAtual.includes(URL_DISTRATO_PARAM.EDICAO_DISTRATO) &&
        !urlAtual.includes(URL_DISTRATO_PARAM.CADASTRO_DISTRATO);
    };

    if (isEntePrivado() && fluxoEntePrivado) {
      setTipoFluxoFilter(TipoFluxo.DESCENTRALIZADO);
      setTipoEnteFilter(TipoContrato.ENTE_PRIVADO);
      dispatch(loadContratoEntePrivado(Number(id), VersaoContratoEnum.CONTRATO));
    }
  }, [dispatch, fluxoEntePrivado, id]);

  const handleDocumentoParams = () => {
    const filters = {
      tipoFluxo: tipoFluxoFilter,
      tipoContrato: tipoEnteFilter,
      programa: programasFilter,
    };
    localStorage.setItem('documentoFiltersEntePrivado', JSON.stringify(filters));
    const url = `${modelosDocumentosContratoRoute.path}?busca=true`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const { pathname } = location;
    switch (true) {
      case pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO):
      case pathname.includes(URL_ADITIVO_PARAM.MODAL_EDICAO_ADITIVO):
        dispatch(openModalEntePrivadoAditivo());
        break;
      case pathname.includes(URL_DISTRATO_PARAM.CADASTRO_DISTRATO):
      case pathname.includes(URL_DISTRATO_PARAM.MODAL_EDICAO_DISTRATO):
        dispatch(openModalEntePrivadoDistrato());
        break;
      default:
        break;
    }
  }, [dispatch, location, location.pathname, params.idAditivo, params.idDistrato]);

  return (
    <>
      <Container className='componentsDescentralizado__container'>
        <Box className='componentsDescentralizado__box'>
          <Typography
            variant='h5'
            className='componentsDescentralizado__title'
          >
            {languageValues.documentos}
          </Typography>
          <Typography
            variant='h6'
            className='componentsDescentralizado__sub'
          >
            {languageValues.facaDownloadEUploadContrato}
          </Typography>
        </Box>
      </Container>

      <Card className='componentsDescentralizado__card'>
        <AccordionSummary
          className='componentsDescentralizado__accordionSummary'
          expandIcon={<Icon style={{ color: '#FFFFFF' }} />}
        >
          <Grid container spacing={1}>
            <Grid item xs={4} md={4}>
              <Typography className='componentsDescentralizado__accordionSummary-column-title'>
                {languageValues.cadastroContratoCodigo}
              </Typography>
            </Grid>
            <Grid item xs={3} md={3}>
              <Typography className='componentsDescentralizado__accordionSummary-column-title'>
                {languageValues.cadastroContratoVersao}
              </Typography>
            </Grid>
            <Grid item xs={3} md={3}>
              <Typography className='componentsDescentralizado__accordionSummary-column-title'>
                {languageValues.cadastroContratoSituacao}
              </Typography>
            </Grid>
            <Grid item xs={1} md={1}>
              <Typography className='componentsDescentralizado__accordionSummary-column-title'></Typography>
            </Grid>
            <Grid item xs={1} md={1}>
              <Typography className='componentsDescentralizado__accordionSummary-column-title color-actions'>
                {languageValues.cadastroContratoAcoes}
              </Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        <Box>
          <Typography
            variant='h6'
            className='componentsDescentralizado__adendos-title'
          ></Typography>
          {adendoAll.map((adendo) => (
            <ControlledAccordions
              key={adendo.id}
              id={adendo.id}
              ativo={adendo.ativo}
              codigo={adendo.codigo}
              flgStatus={adendo.flgStatus}
              idDocAssinado={adendo.idDocAssinado}
              idDocExtrato={adendo.idDocExtrato}
              idDocPlanoTrabalho={adendo.idDocPlanoTrabalho}
              incluiPlanoTrabalho={adendo.incluiPlanoTrabalho}
              dataAlteracao={adendo.dataAlteracao}
              versaoContrato={adendo.versaoContrato}
            />
          ))}
        </Box>
        <Box>
          <Row>
            <Grid
              container
              spacing={1}
              className='componentsDescentralizado__container-button-route'
            >
              <Grid item>
                <CustomButton
                  theme='pufvYellow'
                  className={'button-route__modelos-documentos'}
                  label={'Modelos de documentos'}
                  disabled={false}
                  onClick={() => { handleDocumentoParams(); }}
                />
              </Grid>
              <Grid item>
                <CustomButton
                  theme='pufvYellow'
                  className={'button-route__criar-distrato'}
                  label={'Criar distrato'}
                  onClick={() => {
                    history.push(
                      createDistratoDescentralizadoRoute.getURLWithParams(
                        CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
                        CONTRATO_ETAPAS_PARAMS_URL.CADASTRO,
                        id,
                        CONTRATO_PASSOS.DOCUMENTOS
                      )
                    );
                    dispatch(openModalEntePrivadoDistrato());
                  }}
                  disabled={!adendoObject.geraDistrato}
                />
              </Grid>
              <Grid item>
                <CustomButton
                  theme='pufvYellow'
                  className={'button-route__criar-aditivo'}
                  label={'Criar aditivo'}
                  onClick={() => {
                    history.push(
                      createAditivoDescentralizadoRoute.getURLWithParams(
                        CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
                        CONTRATO_ETAPAS_PARAMS_URL.CADASTRO,
                        id,
                        CONTRATO_PASSOS.DOCUMENTOS
                      )
                    );
                    dispatch(openModalEntePrivadoAditivo());
                  }}
                  disabled={!adendoObject.geraAditivo}
                />
              </Grid>
            </Grid>
          </Row>
        </Box>
      </Card>
    </>
  );
};

